/**
 * CR-8612: cost labels and part / supplier names on the PPV variance bar
 * charts (Cost tab → PPV, Higher Cost and Higher Savings, by supplier and by
 * part).
 *
 * - A cost label sits inside the end of its bar, as before, when it fits.
 *   A bar too short for it (£42 beside £26,117) puts it just outside the bar
 *   end instead — the inside position landed it on the axis names.
 * - The name axis is sized to the longest name (within limits) so names show
 *   in full; a name still too long is shortened with "…" and the full name is
 *   on hover.
 *
 * Positions are estimated from character counts — Recharts does not measure
 * label text — with a generous per-character width so an estimate errs
 * towards "outside".
 */

/** Average glyph width of an 11px semi-bold cost label, in px. */
const LABEL_CHAR_PX = 7;
/** Gap between a label and the bar end / bar edge, in px. */
const LABEL_GAP_PX = 5;
/** Average glyph width of a 10px axis name, in px. */
const AXIS_CHAR_PX = 6.2;
/** Room for the axis tick line and padding, in px. */
const AXIS_PADDING_PX = 14;
export const AXIS_MIN_PX = 60;
export const AXIS_MAX_PX = 200;

export interface LabelPlacement {
  x: number;
  textAnchor: 'start' | 'end';
  /** True when the label sits inside the bar. */
  inside: boolean;
}

/**
 * Where a bar's cost label goes.
 *
 * @param bar - the bar rectangle Recharts reports (width can be negative for a negative value)
 * @param text - the formatted label
 * @param positive - true for a bar growing right from zero (cost, or zero), false for one growing left (a saving)
 * @returns the label's x, anchor and whether it is inside the bar
 */
export function labelPlacement(
  bar: { x: number; width: number },
  text: string,
  positive: boolean,
): LabelPlacement {
  const left = Math.min(bar.x, bar.x + bar.width);
  const right = Math.max(bar.x, bar.x + bar.width);
  const needed = text.length * LABEL_CHAR_PX + LABEL_GAP_PX * 2;
  if (right - left >= needed) {
    // Inside the bar's far end, as before.
    return positive
      ? { x: right - LABEL_GAP_PX, textAnchor: 'end', inside: true }
      : { x: left + LABEL_GAP_PX, textAnchor: 'start', inside: true };
  }
  // Just past the bar's far end, away from the name axis.
  return positive
    ? { x: right + LABEL_GAP_PX, textAnchor: 'start', inside: false }
    : { x: left - LABEL_GAP_PX, textAnchor: 'end', inside: false };
}

/**
 * Width of the name axis and how many characters fit in it.
 *
 * @param names - the names on the axis
 * @returns `{ width, maxChars }` — names longer than maxChars are shortened
 */
export function nameAxisLayout(names: string[]): { width: number; maxChars: number } {
  const longest = names.reduce((n, s) => Math.max(n, s.length), 0);
  const wanted = Math.ceil(longest * AXIS_CHAR_PX + AXIS_PADDING_PX);
  const width = Math.min(AXIS_MAX_PX, Math.max(AXIS_MIN_PX, wanted));
  return { width, maxChars: Math.floor((width - AXIS_PADDING_PX) / AXIS_CHAR_PX) };
}

/**
 * A name as the axis shows it.
 *
 * @param name - the full name
 * @param maxChars - from {@link nameAxisLayout}
 * @returns the name, or its start plus "…" when it does not fit
 */
export function axisName(name: string, maxChars: number): string {
  return name.length > maxChars ? `${name.slice(0, Math.max(1, maxChars - 1))}…` : name;
}

interface CostLabelProps {
  x?: number | string;
  y?: number | string;
  width?: number | string;
  height?: number | string;
  value?: number | string;
}

/**
 * LabelList `content` for a variance bar: the formatted cost, inside or just
 * outside the bar end per {@link labelPlacement}.
 *
 * The side comes from the bar's own value, not the panel: a zero bar on the
 * Savings panel sits at the axis edge when every value is zero, and "outside
 * to the left" would then land on the names.
 *
 * @param format - formats the variance (display currency)
 * @returns a label renderer
 */
export function costLabel(format: (value: number) => string) {
  return function CostLabel({ x, y, width, height, value }: CostLabelProps) {
    const amount = Number(value) || 0;
    const text = format(amount);
    const place = labelPlacement({ x: Number(x) || 0, width: Number(width) || 0 }, text, amount >= 0);
    return (
      <text
        x={place.x}
        y={(Number(y) || 0) + (Number(height) || 0) / 2}
        dy="0.35em"
        textAnchor={place.textAnchor}
        fontSize={11}
        fontWeight={600}
        fill="#1f2937"
        data-testid="ppv-cost-label"
        data-inside={place.inside ? 'true' : 'false'}
      >
        {text}
      </text>
    );
  };
}

interface NameTickProps {
  x?: number | string;
  y?: number | string;
  payload?: { value?: unknown };
}

/**
 * YAxis `tick` for the name axis: the name in full when it fits, otherwise
 * shortened with the full name on hover.
 *
 * @param maxChars - from {@link nameAxisLayout}
 * @returns a tick renderer
 */
export function nameTick(maxChars: number) {
  return function NameTick({ x, y, payload }: NameTickProps) {
    const full = String(payload?.value ?? '');
    return (
      <text x={Number(x) || 0} y={Number(y) || 0} dy="0.35em" textAnchor="end" fontSize={10} fill="#666" data-testid="ppv-axis-name">
        <title>{full}</title>
        {axisName(full, maxChars)}
      </text>
    );
  };
}

/**
 * DASH-FIXES: the value axis of a variance panel is anchored at zero on the side
 * the bars grow from: 0 to a rounded maximum for a cost panel (Recharts' own
 * default for a number axis, so those panels draw as before), a rounded minimum
 * to 0 for a savings panel. Without the anchor a savings panel holding one bar
 * collapsed its axis onto that one value, printing "-1.9K" at every tick.
 *
 * @param positive - true for a Higher Cost panel, false for a Higher Savings panel
 * @returns the XAxis domain
 */
export function valueAxisDomain(positive: boolean): [number | 'auto', number | 'auto'] {
  return positive ? [0, 'auto'] : ['auto', 0];
}

/** A month's PPV within this many units of zero (below it) does not pull the axis negative. */
const OVER_TIME_NEGATIVE_FLOOR = -1;

/**
 * PPV-POLISH (spec 04 §4.3): the over-time chart's value axis. It starts at 0
 * unless some month's PPV is below −1 unit; then it is exactly what it was
 * before: Recharts' own default `[0, 'auto']` (recharts `defaultNumericDomain`),
 * which Recharts widens to take in the negative months. A −£0.30 month (FX dust) once stretched the axis to −5.5K and squeezed
 * the real bars into the top of the chart. `allowDataOverflow` keeps Recharts
 * from widening a set domain back out to that month; its bar is under a unit,
 * too small to draw at the chart's scale anyway.
 *
 * @param ppvs - every month's `ppv`
 * @returns the value YAxis's `domain` and `allowDataOverflow`
 */
export function overTimeValueAxis(ppvs: number[]): {
  domain: [number | 'auto', number | 'auto'];
  allowDataOverflow: boolean;
} {
  const genuineNegative = ppvs.some((v) => v < OVER_TIME_NEGATIVE_FLOOR);
  return { domain: [0, 'auto'], allowDataOverflow: !genuineNegative };
}

/**
 * PPV-POLISH (spec 04 §4.3): decimals for a month's PPV in the over-time
 * tooltip. A figure under one unit (not zero) shows pence — `-£0.30`, never the
 * `-£0` whole units would print; 0 and everything else as before.
 *
 * @param value - the month's PPV
 * @returns decimals to pass to `formatCurrency`
 */
export function monthPPVDecimals(value: number): number {
  return value !== 0 && Math.abs(value) < 1 ? 2 : 0;
}
